// The help centre's public pages (/help, /help/<section>, /help/<section>/<category>, /help/articles/<slug>) for
// src/lib/seo/pages.ts: titles, descriptions, link-preview images (one card per section, made by
// scripts/brand-assets/guide-og.mjs), sitemap dates and JSON-LD (Article, BreadcrumbList, FAQPage where an article
// shows questions, CollectionPage + ItemList for the listings, and a SearchAction for /help?q=). Only listed where
// the help centre is on (src/lib/helpFlag.ts). A category without articles has no page.

import {
  ALL_HELP,
  HELP_CATEGORIES,
  HELP_LANGS,
  HELP_PATH,
  HELP_SECTIONS,
  HELP_SLUGS,
  categoryArticles,
  getHelpArticle,
  helpArticlePath,
  helpCategoryPath,
  helpLangs,
  helpMachineLangs,
  helpSectionPath,
  isSeparateSection,
  label,
  listHelp,
  type HelpArticle,
  type HelpSectionId,
} from "@/content/help";
import { plainInline } from "@/content/guides/body";
import i18n from "@/i18n";
import { LANG_TAG, type OgImage, type SeoLang } from "./site";
import type { PublicPage, StructuredDataContext } from "./pages";

/** A help card (1200x630, public/images/og/help/<name>.jpg). */
export const helpOgImage = (name: "index" | HelpSectionId, alt: string): OgImage => ({
  path: `/images/og/help/${name}.jpg`,
  width: 1200,
  height: 630,
  type: "image/jpeg",
  alt,
});

/** UI copy (src/i18n/locales/<lang>.json, help.*) in a page language; English for languages the UI doesn't have. */
const t = (key: string, lang: SeoLang, vars?: Record<string, string>) => i18n.getFixedT(lang)(`help.${key}`, vars) as string;

export const sectionTitle = (s: HelpSectionId, lang: SeoLang = "en") => t(`sections.${s}.title`, lang);
const sectionBlurb = (s: HelpSectionId, lang: SeoLang = "en") => t(`sections.${s}.blurb`, lang);

const orgRef = (origin: string) => ({ "@id": `${origin}/#organization` });
const websiteRef = (origin: string) => ({ "@type": "WebSite", "@id": `${origin}/#website`, name: "Photohawk", url: `${origin}/` });

const crumbs = (url: string, items: { name: string; url: string }[]) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumbs`,
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
});

const latest = (dates: string[]) => dates.reduce((a, b) => (a > b ? a : b), "");

/** The breadcrumbs' first items: the hub, then the section; a separate section (the Photohawk app's) starts at itself. */
const sectionCrumbs = (origin: string, s: HelpSectionId, lang: SeoLang, url = `${origin}${helpSectionPath(s)}`) => [
  ...(isSeparateSection(s) ? [] : [{ name: t("breadcrumbHome", lang), url: `${origin}${HELP_PATH}` }]),
  { name: sectionTitle(s, lang), url },
];

const itemList = (origin: string, articles: HelpArticle[]) => ({
  "@type": "ItemList",
  itemListElement: articles.map((a, i) => ({ "@type": "ListItem", position: i + 1, url: `${origin}${helpArticlePath(a.slug)}`, name: a.title })),
});

/** The JSON-LD for one article. */
export function helpArticleStructuredData(a: HelpArticle, ctx: StructuredDataContext): object[] {
  const { origin, url, image } = ctx;
  const cat = HELP_CATEGORIES.find((c) => c.id === a.category)!;
  const nodes: object[] = [
    {
      "@type": "WebPage",
      "@id": `${url}#page`,
      url,
      name: ctx.title,
      description: ctx.description,
      inLanguage: LANG_TAG[a.lang],
      isPartOf: websiteRef(origin),
      breadcrumb: { "@id": `${url}#breadcrumbs` },
    },
    {
      "@type": "TechArticle",
      "@id": `${url}#article`,
      headline: a.title,
      description: a.description,
      inLanguage: LANG_TAG[a.lang],
      mainEntityOfPage: { "@id": `${url}#page` },
      image: { "@type": "ImageObject", url: image, width: 1200, height: 630 },
      dateModified: a.updated,
      author: orgRef(origin),
      publisher: orgRef(origin),
      wordCount: a.words,
      articleSection: label(cat.title, a.lang),
    },
    crumbs(url, [
      ...sectionCrumbs(origin, a.section, a.lang),
      { name: label(cat.title, a.lang), url: `${origin}${helpCategoryPath(a.section, a.category)}` },
      { name: a.title, url },
    ]),
  ];
  if (a.faq.length) {
    nodes.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: a.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: plainInline(f.a) } })),
    });
  }
  return nodes;
}

/** Every help page, for publicPages() where the help centre is on. */
export function helpPages(): PublicPage[] {
  if (!ALL_HELP.length) return [];
  const langs = HELP_LANGS.length ? HELP_LANGS : (["en"] as SeoLang[]);
  const hub: PublicPage = {
    path: HELP_PATH,
    langs,
    seo: (lang) => ({ title: t("seo.title", lang), description: t("seo.description", lang) }),
    image: helpOgImage("index", "Photohawk help centre"),
    type: "website",
    indexable: true,
    changefreq: "weekly",
    priority: 0.6,
    lastmod: latest(ALL_HELP.filter((a) => !isSeparateSection(a.section)).map((a) => a.updated)),
    structuredData: ({ origin, url, title, description, lang }) => [
      {
        "@type": "CollectionPage",
        "@id": `${url}#page`,
        url,
        name: title,
        description,
        inLanguage: LANG_TAG[lang],
        isPartOf: websiteRef(origin),
        publisher: orgRef(origin),
        breadcrumb: { "@id": `${url}#breadcrumbs` },
        // The hub lists its own sections' articles; a separate section (the Photohawk app's) has its own page.
        mainEntity: itemList(origin, listHelp(lang).filter((a) => !isSeparateSection(a.section))),
        // The help search (client-side): /help?q=…
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${origin}${HELP_PATH}?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
      crumbs(url, [{ name: t("breadcrumbHome", lang), url }]),
    ],
  };

  const sections = HELP_SECTIONS.filter((s) => ALL_HELP.some((a) => a.section === s.id)).map(
    (s): PublicPage => ({
      path: helpSectionPath(s.id),
      langs,
      seo: (lang) => ({ title: `${sectionTitle(s.id, lang)} | ${t("seo.suffix", lang)}`, description: sectionBlurb(s.id, lang) }),
      image: helpOgImage(s.id, sectionTitle(s.id)),
      type: "website",
      indexable: true,
      changefreq: "weekly",
      priority: 0.5,
      lastmod: latest(ALL_HELP.filter((a) => a.section === s.id).map((a) => a.updated)),
      structuredData: ({ origin, url, title, description, lang }) => [
        {
          "@type": "CollectionPage",
          "@id": `${url}#page`,
          url,
          name: title,
          description,
          inLanguage: LANG_TAG[lang],
          isPartOf: websiteRef(origin),
          breadcrumb: { "@id": `${url}#breadcrumbs` },
          mainEntity: itemList(origin, listHelp(lang).filter((a) => a.section === s.id)),
        },
        crumbs(url, sectionCrumbs(origin, s.id, lang, url)),
      ],
    }),
  );

  const categories = HELP_CATEGORIES.filter((c) => categoryArticles(c.id).length).map(
    (c): PublicPage => ({
      path: helpCategoryPath(c.section, c.id),
      langs,
      seo: (lang) => ({ title: `${label(c.title, lang)} | ${t("seo.suffix", lang)}`, description: label(c.description, lang) }),
      image: helpOgImage(c.section, label(c.title, "en")),
      type: "website",
      indexable: true,
      changefreq: "weekly",
      priority: 0.5,
      lastmod: latest(ALL_HELP.filter((a) => a.category === c.id).map((a) => a.updated)),
      structuredData: ({ origin, url, title, description, lang }) => [
        {
          "@type": "CollectionPage",
          "@id": `${url}#page`,
          url,
          name: title,
          description,
          inLanguage: LANG_TAG[lang],
          isPartOf: websiteRef(origin),
          breadcrumb: { "@id": `${url}#breadcrumbs` },
          mainEntity: itemList(origin, categoryArticles(c.id, lang)),
        },
        crumbs(url, [...sectionCrumbs(origin, c.section, lang), { name: label(c.title, lang), url }]),
      ],
    }),
  );

  const articles = HELP_SLUGS.map((slug): PublicPage => {
    const en = getHelpArticle(slug, "en")!;
    return {
      path: helpArticlePath(slug),
      langs: helpLangs(slug),
      noindexLangs: helpMachineLangs(slug),
      seo: (lang) => {
        const a = getHelpArticle(slug, lang)!;
        return { title: `${a.title} | ${t("seo.suffix", lang)}`, description: a.description };
      },
      image: helpOgImage(en.section, en.title),
      type: "article",
      indexable: true,
      changefreq: "monthly",
      priority: 0.5,
      lastmod: latest(ALL_HELP.filter((a) => a.slug === slug).map((a) => a.updated)),
      structuredData: (ctx) => helpArticleStructuredData(getHelpArticle(slug, ctx.lang)!, ctx),
    };
  });

  return [hub, ...sections, ...categories, ...articles];
}
